/**
 * Cart v2 (docs/cart-v2-README.md): shared cart state over the Cart AJAX API,
 * plus the product card "+ Add" / stepper.
 *
 * window.KMCart exposes the cart and add/change helpers so the quick-add sheet and
 * cart drawer can share one source of truth. Every change fires a `km:cart` event.
 */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var KM = window.KMCart = window.KMCart || {};
  var INA_PROP = '_if_sold_out';
  // Default "If not available" choice per product type, same as the product page.
  var INA_DEFAULT = {
    produce: 'Contact me',
    kitchen: 'Contact me',
    replace: 'Go with shopper recommendation'
  };

  KM.cart = null;
  KM.INA_PROP = INA_PROP;
  KM.INA_DEFAULT = INA_DEFAULT;

  // Marks our own requests so the fetch watcher below only reacts to the theme's.
  var OWN_HEADER = 'X-KM-Cart';

  // Each request is numbered when it is sent. Cart requests can overlap (e.g. a refresh triggered
  // by another script while the customer removes an item); setCart ignores a reply that was sent
  // before the one already shown, so an older "1 item" can't overwrite a newer empty cart.
  var sentSeq = 0;
  var shownSeq = 0;

  function api(path, body) {
    var headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    headers[OWN_HEADER] = '1';
    var seq = ++sentSeq;
    return fetch(root + path, {
      method: body ? 'POST' : 'GET',
      headers: headers,
      body: body ? JSON.stringify(body) : undefined
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) throw json;
        if (json && typeof json === 'object') Object.defineProperty(json, '_kmSeq', { value: seq });
        return json;
      });
    });
  }

  // Keep the theme's (now hidden) side cart current: zipcode-handler.js reads its local-only
  // warning when guarding checkout.
  function syncThemeDrawer(cart) {
    if (window.ajaxCart && document.getElementById('cart')) {
      try { window.ajaxCart.load(false, false, false, true); } catch (e) {}
    }
    return cart;
  }

  function setCart(cart) {
    if (cart && cart._kmSeq) {
      if (cart._kmSeq < shownSeq) return KM.cart;
      shownSeq = cart._kmSeq;
    }
    KM.cart = cart;
    updateCounts(cart.item_count);
    renderCards();
    renderBar();
    refreshDrawer();
    document.dispatchEvent(new CustomEvent('km:cart', { detail: cart }));
    return cart;
  }

  KM.refresh = function () {
    return api('cart.js').then(setCart);
  };

  // A page shown again from the browser's back/forward cache, or a tab the customer comes back
  // to, may hold an old cart (changed on another page or tab): fetch the current one.
  window.addEventListener('pageshow', function (e) { if (e.persisted) KM.refresh().catch(function () {}); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && KM.cart) KM.refresh().catch(function () {});
  });

  KM.add = function (variantId, quantity, properties) {
    return api('cart/add.js', {
      items: [{ id: Number(variantId), quantity: quantity || 1, properties: properties || {} }]
    }).then(KM.refresh).then(syncThemeDrawer);
  };

  // /cart/change.js returns the full cart, so no second fetch is needed.
  KM.changeLine = function (key, quantity, properties) {
    var body = { id: key, quantity: quantity };
    if (properties) body.properties = properties;
    return api('cart/change.js', body).then(setCart).then(syncThemeDrawer);
  };

  KM.linesFor = function (variantId) {
    if (!KM.cart) return [];
    return KM.cart.items.filter(function (item) { return String(item.variant_id) === String(variantId); });
  };

  KM.qtyFor = function (variantId) {
    return KM.linesFor(variantId).reduce(function (sum, item) { return sum + item.quantity; }, 0);
  };

  KM.showError = function (err) {
    var message = (err && (err.description || err.message)) || 'Something went wrong. Please try again.';
    window.dispatchEvent(new CustomEvent('showAlert', { detail: { message: message, type: 'error' } }));
  };

  // Header cart badges, same elements the theme's ajaxCart updates.
  function updateCounts(count) {
    document.querySelectorAll('.cart-count').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('hidden', !(count > 0));
    });
  }

  /* ---------- Product cards ---------- */

  function renderCards() {
    document.querySelectorAll('[data-km-card]').forEach(function (card) {
      var qty = cardLines(card).reduce(function (sum, item) { return sum + item.quantity; }, 0);
      var stepper = card.querySelector('[data-km-stepper]');
      card.classList.toggle('is-in-cart', qty > 0);
      if (!stepper) return;
      stepper.hidden = qty === 0;
      stepper.querySelector('[data-km-qty]').textContent = qty;
    });
  }

  function cardInfo(card) {
    return {
      card: card,
      productId: card.getAttribute('data-product-id'),
      variantId: card.getAttribute('data-variant-id'),
      handle: card.getAttribute('data-handle'),
      ina: card.getAttribute('data-km-ina') || 'none',
      hard: card.hasAttribute('data-km-hard'),
      title: card.getAttribute('data-title'),
      image: card.getAttribute('data-image'),
      price: Number(card.getAttribute('data-price')) || 0,
      compare: Number(card.getAttribute('data-compare')) || 0,
      options: readVariants(card)
    };
  }

  // Products with several options carry their variants as JSON for the sheet's pills.
  function readVariants(card) {
    var el = card.querySelector('[data-km-variants]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // A multi-option card counts every option of its product that's in the cart.
  function cardLines(card) {
    if (!KM.cart) return [];
    if (!card.hasAttribute('data-km-multi')) return KM.linesFor(card.getAttribute('data-variant-id'));
    var productId = card.getAttribute('data-product-id');
    return KM.cart.items.filter(function (item) { return String(item.product_id) === String(productId); });
  }

  function busy(card, promise) {
    card.classList.add('is-busy');
    return promise.catch(KM.showError).finally(function () { card.classList.remove('is-busy'); });
  }

  // "+ Add": items with replacement options go through the quick-add sheet when it is
  // available; everything else is added straight away.
  function onAdd(card) {
    var info = cardInfo(card);
    if ((info.ina !== 'none' || info.options) && KM.openQuickAdd(info)) return;
    var props = {};
    if (INA_DEFAULT[info.ina]) props[INA_PROP] = INA_DEFAULT[info.ina];
    busy(card, KM.add(info.variantId, 1, props));
  }

  // The card stepper never reopens the sheet: it adjusts the most recent line for this
  // variant, which keeps that line's "If not available" choice.
  function onStep(card, delta) {
    var lines = cardLines(card);
    var line = lines[lines.length - 1];
    if (!line) return;
    busy(card, KM.changeLine(line.key, Math.max(0, line.quantity + delta)));
  }

  document.addEventListener('click', function (e) {
    var card = e.target.closest('[data-km-card]');
    if (!card) return;
    if (e.target.closest('[data-km-add]')) { e.preventDefault(); onAdd(card); }
    else if (e.target.closest('[data-km-inc]')) { e.preventDefault(); onStep(card, 1); }
    else if (e.target.closest('[data-km-dec]')) { e.preventDefault(); onStep(card, -1); }
  });

  /* ---------- Money ---------- */

  KM.money = function (cents) {
    var el = document.querySelector('[data-money-format]');
    var format = (el && el.getAttribute('data-money-format')) || '${{amount}}';
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    function num(decimals, thousands, point) {
      var parts = ((Number(cents) || 0) / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[0] + (parts[1] ? point + parts[1] : '');
    }
    var value = {
      amount_no_decimals: num(0, ',', '.'),
      amount_with_comma_separator: num(2, '.', ','),
      amount_no_decimals_with_comma_separator: num(0, '.', ',')
    }[match ? match[1] : 'amount'] || num(2, ',', '.');
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  /* ---------- Quick-add sheet ---------- */

  // edit = { key, quantity, pref, onClose } reopens the sheet for an existing cart line
  // ("Change" in the cart), saving with "Update cart" instead of adding a new line.
  var qa = { el: null, info: null, edit: null, qty: 1, returnFocus: null, hideTimer: null };

  function qaPart(name) { return qa.el.querySelector('[data-km-qa-' + name + ']'); }

  KM.openQuickAdd = function (info, edit) {
    qa.el = qa.el || document.querySelector('[data-km-qa]');
    if (!qa.el) return false;
    var template = qa.el.querySelector('template[data-km-ina-tpl="' + info.ina + '"]');
    var hasOptions = !edit && info.options && info.options.variants && info.options.variants.length > 1;
    if (!template && !hasOptions) return false;

    qa.info = info;
    qa.edit = edit || null;
    qa.qty = edit ? edit.quantity : 1;

    qaPart('title').textContent = info.title || '';
    renderVariantPills(hasOptions ? info.options : null);
    renderProductBits();

    var options = qaPart('options');
    options.innerHTML = '';
    if (template) options.appendChild(template.content.cloneNode(true));
    // Hard-to-get items: nothing picked in advance, the customer has to choose before adding.
    qa.needsChoice = !!(info.hard && !edit && template);
    if (qa.needsChoice) {
      options.querySelectorAll('input[type="radio"]').forEach(function (r) { r.checked = false; });
      var title = options.querySelector('.km-ina__title');
      if (title && !options.querySelector('.km-ina__hard')) {
        var note = document.createElement('p');
        note.className = 'km-ina__note km-ina__hard';
        note.textContent = "Often hard to find. Choose what we should do if it's not on the shelf.";
        title.parentNode.insertBefore(note, title.nextSibling);
      }
    }
    if (edit && edit.pref) {
      options.querySelectorAll('input[type="radio"]').forEach(function (input) {
        if (input.value === edit.pref) input.checked = true;
      });
    }
    qaPart('scroll').scrollTop = 0;

    renderQuickAdd();
    showSheet();
    return true;
  };

  function renderProductBits() {
    var info = qa.info;
    var img = qaPart('img');
    img.src = info.image || '';
    img.alt = info.title || '';
    qaPart('price').textContent = KM.money(info.price);
    qaPart('price').classList.toggle('is-sale', info.compare > info.price);
    qaPart('compare').textContent = info.compare > info.price ? KM.money(info.compare) : '';
  }

  // Option pills (e.g. Size: 3lbs Bag / 10lbs Bag). Picking one switches the variant that gets
  // added, with its own price and image.
  function renderVariantPills(options) {
    var wrap = qaPart('variants');
    var pills = qaPart('pills');
    pills.innerHTML = '';
    wrap.hidden = !options;
    if (!options) return;
    qaPart('optname').textContent = options.name || 'Options';
    options.variants.forEach(function (variant) {
      var pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'km-vpill';
      pill.textContent = variant.title;
      pill.setAttribute('role', 'radio');
      pill.setAttribute('data-km-qa-variant', variant.id);
      pill.disabled = !variant.available;
      pills.appendChild(pill);
    });
    var current = options.variants.filter(function (v) { return String(v.id) === String(qa.info.variantId) && v.available; })[0]
      || options.variants.filter(function (v) { return v.available; })[0];
    if (current) selectVariant(current);
  }

  function selectVariant(variant) {
    qa.info.variantId = variant.id;
    qa.info.price = variant.price;
    qa.info.compare = variant.compare;
    if (variant.image) qa.info.image = variant.image;
    qaPart('pills').querySelectorAll('[data-km-qa-variant]').forEach(function (pill) {
      var on = pill.getAttribute('data-km-qa-variant') === String(variant.id);
      pill.classList.toggle('is-on', on);
      pill.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    renderProductBits();
  }

  function renderQuickAdd() {
    qaPart('qty').textContent = qa.qty;
    qaPart('dec').disabled = qa.qty <= 1;
    qaPart('submit').textContent = (qa.edit ? 'Update cart' : 'Add to cart') + ' · ' + KM.money(qa.info.price * qa.qty);
    qaPart('submit').disabled = !!qa.needsChoice && !qaPart('options').querySelector('input[type="radio"]:checked');
  }

  // A choice made in the sheet unlocks "Add to cart" for hard-to-get items.
  document.addEventListener('change', function (e) {
    if (qa.el && qa.el.contains(e.target) && e.target.matches('input[type="radio"]')) renderQuickAdd();
  });

  function showSheet() {
    clearTimeout(qa.hideTimer);
    qa.returnFocus = document.activeElement;
    qa.el.hidden = false;
    void qa.el.offsetWidth; // start the slide-up transition from the closed position
    qa.el.classList.add('is-open');
    lockScroll();
    qa.el.querySelector('.km-x').focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('km:sheet', { detail: { open: true } }));
  }

  KM.closeQuickAdd = function () {
    if (!qa.el || !qa.el.classList.contains('is-open')) return;
    var edit = qa.edit;
    qa.el.classList.remove('is-open');
    lockScroll();
    qa.hideTimer = setTimeout(function () { qa.el.hidden = true; }, 320);
    if (qa.returnFocus && document.contains(qa.returnFocus)) qa.returnFocus.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('km:sheet', { detail: { open: false } }));
    if (edit && edit.onClose) edit.onClose();
  };

  function submitQuickAdd(form) {
    var checked = form.querySelector('input[name="properties[_if_sold_out]"]:checked');
    if (qa.needsChoice && !checked) return;
    var props = {};
    if (checked) props[INA_PROP] = checked.value;
    var submit = qaPart('submit');
    submit.classList.add('is-busy');
    var request = qa.edit
      ? KM.changeLine(qa.edit.key, qa.qty, props)
      : KM.add(qa.info.variantId, qa.qty, props);
    request.then(KM.closeQuickAdd).catch(KM.showError).finally(function () {
      submit.classList.remove('is-busy');
    });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-km-qa]')) return;
    var pill = e.target.closest('[data-km-qa-variant]');
    if (pill) {
      var id = pill.getAttribute('data-km-qa-variant');
      var variant = qa.info.options.variants.filter(function (v) { return String(v.id) === id; })[0];
      if (variant) { selectVariant(variant); renderQuickAdd(); }
      return;
    }
    if (e.target.closest('[data-km-qa-close]')) KM.closeQuickAdd();
    else if (e.target.closest('[data-km-qa-inc]')) { qa.qty++; renderQuickAdd(); }
    else if (e.target.closest('[data-km-qa-dec]')) { qa.qty = Math.max(1, qa.qty - 1); renderQuickAdd(); }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-km-qa-form]');
    if (!form) return;
    e.preventDefault();
    submitQuickAdd(form);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (qa.el && qa.el.classList.contains('is-open')) KM.closeQuickAdd();
    else KM.closeCart();
  });

  function lockScroll() {
    document.documentElement.classList.toggle('km-lock', !!document.querySelector('.km-sheet.is-open:not(.km-drawer--page)'));
  }

  /* ---------- Cart drawer ---------- */

  var dr = { el: document.querySelector('[data-km-drawer]'), seq: 0, returnFocus: null, hideTimer: null };
  KM.drawer = !!dr.el;
  // On /cart the same section is the page itself: always open, nothing to close.
  var drPage = !!(dr.el && dr.el.classList.contains('km-drawer--page'));
  var drSection = dr.el ? dr.el.getAttribute('data-section-id') : '';

  // Swap in fresh [data-km-dr-part] blocks from the Section Rendering API. The delivery block
  // and the discount field are left alone so the calendar app and typed input keep their state.
  function refreshDrawer() {
    if (!dr.el) return;
    var seq = ++dr.seq;
    var headers = {};
    headers[OWN_HEADER] = '1';
    fetch(root + '?sections=' + encodeURIComponent(drSection), { headers: headers })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (seq !== dr.seq || !data[drSection]) return;
        var fresh = new DOMParser().parseFromString(data[drSection], 'text/html');
        dr.el.querySelectorAll('[data-km-dr-part]').forEach(function (part) {
          var next = fresh.querySelector('[data-km-dr-part="' + part.getAttribute('data-km-dr-part') + '"]');
          if (next) part.innerHTML = next.innerHTML;
        });
        renderDrawerState();
      })
      .catch(function () {});
  }

  // Local delivery needs a slot before checkout, when the calendar app requires one.
  function needsDeliverySlot() {
    var configEl = document.querySelector('[data-delivery-config]');
    if (!configEl) return false;
    try {
      var config = JSON.parse(configEl.textContent);
      if (!config.enabled || !config.required) return false;
      var zip = JSON.parse(localStorage.getItem('shopify_zipcode') || 'null');
      return !(zip && zip.deliveryType === 'shipping');
    } catch (e) {
      return false;
    }
  }

  function isShippingZip() {
    return document.documentElement.classList.contains('km-ship-zip');
  }

  function setStep(step) {
    if (!dr.el) return;
    var changed = dr.el.getAttribute('data-step') !== step;
    dr.el.setAttribute('data-step', step);
    var scroll = dr.el.querySelector('.km-dr__step--' + step + ' .km-dr__scroll');
    if (scroll) scroll.scrollTop = 0;
    if (drPage && changed) {
      // Land the step's header just below the sticky site header.
      var header = document.getElementById('header-outer');
      var offset = header ? Math.max(0, header.getBoundingClientRect().bottom) + 16 : 120;
      window.scrollTo({ top: dr.el.getBoundingClientRect().top + window.scrollY - offset });
    }
  }

  // The totals list only cart discounts that already have an amount. Codes that only take effect
  // at checkout (e.g. free shipping) were applied but not shown, so add a row for each of them.
  function renderCodeRows() {
    var tot = dr.el.querySelector('[data-km-dr-part="foot2"] .km-tot');
    if (!tot) return;
    tot.querySelectorAll('[data-km-dc-extra]').forEach(function (row) { row.remove(); });
    var shown = Array.prototype.map.call(tot.querySelectorAll('[data-km-dc-remove]'), function (b) {
      return b.getAttribute('data-km-dc-remove').toLowerCase();
    });
    var anchor = tot.querySelectorAll('.km-tot__row--disc');
    anchor = anchor.length ? anchor[anchor.length - 1] : tot.querySelector('.km-tot__row--sub');
    appliedCodes().forEach(function (code) {
      if (shown.indexOf(code.toLowerCase()) !== -1 || !anchor) return;
      var row = document.createElement('div');
      row.className = 'km-tot__row km-tot__row--disc';
      row.setAttribute('data-km-dc-extra', '');
      var label = document.createElement('span');
      label.className = 'km-tot__disc';
      label.appendChild(document.createTextNode('Discount '));
      var chip = document.createElement('span');
      chip.className = 'km-chip';
      chip.appendChild(document.createTextNode(code));
      var x = document.createElement('button');
      x.type = 'button';
      x.setAttribute('data-km-dc-remove', code);
      x.setAttribute('aria-label', 'Remove discount ' + code);
      x.textContent = '×';
      chip.appendChild(x);
      label.appendChild(chip);
      var amount = document.createElement('span');
      amount.textContent = 'At checkout';
      row.appendChild(label);
      row.appendChild(amount);
      anchor.parentNode.insertBefore(row, anchor.nextSibling);
      anchor = row;
    });
  }

  function renderDrawerState() {
    if (!dr.el || !KM.cart) return;
    renderCodeRows();
    dr.el.classList.toggle('is-empty', KM.cart.item_count === 0);
    if (KM.cart.item_count === 0) setStep('cart');
    var note = dr.el.querySelector('[data-km-note]');
    if (note) note.placeholder = note.getAttribute(isShippingZip() ? 'data-ph-ship' : 'data-ph-local');
    var button = dr.el.querySelector('[data-km-dr-checkout-btn]');
    if (!button) return;
    var attrs = KM.cart.attributes || {};
    var blocked = needsDeliverySlot() && !(attrs.delivery_date && attrs.delivery_timeslot);
    button.classList.toggle('is-blocked', blocked);
    button.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    button.textContent = blocked ? 'Choose a delivery time' : button.getAttribute('data-label');
  }

  KM.openCart = function () {
    if (!dr.el) { window.location.href = root + 'cart'; return; }
    if (drPage) {
      setStep('cart');
      renderDrawerState();
      dr.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    clearTimeout(dr.hideTimer);
    if (!dr.el.classList.contains('is-open')) dr.returnFocus = document.activeElement;
    if (!dr.el.classList.contains('is-open')) setStep('cart');
    dr.el.hidden = false;
    void dr.el.offsetWidth;
    dr.el.classList.add('is-open');
    lockScroll();
    renderDrawerState();
    dr.el.querySelector('.km-dr__step--cart .km-x').focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('km:sheet', { detail: { open: true } }));
  };

  KM.closeCart = function (keepFocus) {
    if (!dr.el || drPage || !dr.el.classList.contains('is-open')) return;
    dr.el.classList.remove('is-open');
    lockScroll();
    dr.hideTimer = setTimeout(function () { dr.el.hidden = true; }, 320);
    if (!keepFocus && dr.returnFocus && document.contains(dr.returnFocus)) dr.returnFocus.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('km:sheet', { detail: { open: false } }));
  };

  function lineAction(line, quantity) {
    line.classList.add('is-busy');
    KM.changeLine(line.getAttribute('data-key'), quantity).catch(function (err) {
      line.classList.remove('is-busy');
      KM.showError(err);
    });
  }

  // "Change": edit this line's "If not available" choice in the quick-add sheet, then come back.
  function changeLinePref(line) {
    var info = {
      variantId: line.getAttribute('data-variant-id'),
      ina: line.getAttribute('data-km-ina'),
      title: line.getAttribute('data-title'),
      image: line.getAttribute('data-image'),
      price: Number(line.getAttribute('data-price')) || 0,
      compare: Number(line.getAttribute('data-compare')) || 0
    };
    KM.closeCart(true);
    KM.openQuickAdd(info, {
      key: line.getAttribute('data-key'),
      quantity: Number(line.getAttribute('data-qty')) || 1,
      pref: line.getAttribute('data-pref'),
      onClose: drPage ? null : KM.openCart
    });
  }

  /* Discount codes: Shopify validates them through /cart/update.js. */

  function appliedCodes() {
    return ((KM.cart && KM.cart.discount_codes) || [])
      .filter(function (d) { return d.applicable; })
      .map(function (d) { return d.code; });
  }

  function discountError(message) {
    var err = dr.el.querySelector('[data-km-dc-error]');
    err.textContent = message || '';
    err.hidden = !message;
    dr.el.querySelector('[data-km-dc-form]').classList.toggle('is-error', !!message);
  }

  function applyDiscount(form) {
    var input = form.querySelector('[data-km-dc-input]');
    var code = input.value.trim();
    if (!code) return;
    var current = appliedCodes();
    if (current.some(function (c) { return c.toLowerCase() === code.toLowerCase(); })) {
      discountError('That code is already applied.');
      return;
    }
    form.classList.add('is-busy');
    discountError('');
    api('cart/update.js', { discount: current.concat(code).join(',') })
      .then(function (cart) {
        var match = (cart.discount_codes || []).filter(function (d) { return d.code.toLowerCase() === code.toLowerCase(); })[0];
        if (match && match.applicable) {
          closeDiscount();
          return setCart(cart);
        }
        discountError("That code isn't valid for this cart.");
        // Don't leave a code Shopify rejected sitting on the cart.
        return api('cart/update.js', { discount: current.join(',') }).then(setCart);
      })
      .catch(KM.showError)
      .finally(function () { form.classList.remove('is-busy'); });
  }

  // Clears the field and collapses back to the "Add a discount code" link.
  function closeDiscount() {
    var form = dr.el.querySelector('[data-km-dc-form]');
    form.querySelector('[data-km-dc-input]').value = '';
    discountError('');
    form.hidden = true;
    dr.el.querySelector('[data-km-dc-open]').hidden = false;
  }

  function removeDiscount(code) {
    var rest = appliedCodes().filter(function (c) { return c.toLowerCase() !== code.toLowerCase(); });
    api('cart/update.js', { discount: rest.join(',') }).then(setCart).catch(KM.showError);
  }

  if (dr.el) {
    dr.el.addEventListener('click', function (e) {
      var t = e.target;
      var line = t.closest('[data-km-line]');
      if (t.closest('[data-km-dr-close]')) {
        // On the cart page "Keep shopping" goes back to the store.
        if (drPage) window.location.href = root;
        else KM.closeCart();
        return;
      }
      if (t.closest('[data-km-dr-continue]')) { setStep('delivery'); return; }
      if (t.closest('[data-km-dr-back]')) { setStep('cart'); return; }
      if (t.closest('[data-km-dc-close]')) { closeDiscount(); return; }
      if (line && t.closest('[data-km-line-inc]')) lineAction(line, Number(line.getAttribute('data-qty')) + 1);
      else if (line && t.closest('[data-km-line-dec]')) lineAction(line, Number(line.getAttribute('data-qty')) - 1);
      else if (line && t.closest('[data-km-line-remove]')) lineAction(line, 0);
      else if (line && t.closest('[data-km-line-change]')) changeLinePref(line);
      else if (t.closest('[data-km-dc-open]')) {
        t.closest('[data-km-dc-open]').hidden = true;
        var form = dr.el.querySelector('[data-km-dc-form]');
        form.hidden = false;
        form.querySelector('input').focus();
      } else if (t.closest('[data-km-dc-remove]')) {
        removeDiscount(t.closest('[data-km-dc-remove]').getAttribute('data-km-dc-remove'));
      }
    });

    dr.el.addEventListener('submit', function (e) {
      if (e.target.matches('[data-km-dc-form]')) {
        e.preventDefault();
        applyDiscount(e.target);
      }
    });

    // Checkout. Shopify checkout has no delivery slots, so everything chosen here (slot via the
    // calendar app, receive method, note; discounts are already on the cart) is saved to the
    // cart first, then we go to /checkout. Capture phase so this runs before the calendar
    // app's own check.
    document.addEventListener('submit', function (e) {
      if (!e.target.matches || !e.target.matches('[data-km-dr-checkout]')) return;
      var form = e.target;
      var button = form.querySelector('[data-km-dr-checkout-btn]');

      // Shipping customers with local-only items: let zipcode-handler.js block and explain.
      if (isShippingZip() && document.querySelector('[data-km-local-warning]')) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      if (button.classList.contains('is-blocked')) {
        pickFirstDay();
        return;
      }

      var receive = dr.el.querySelector('[data-km-receive]:checked');
      var attributes = {};
      if (receive && !isShippingZip()) attributes['Delivery preference'] = receive.value;
      var note = dr.el.querySelector('[data-km-note]');
      button.classList.add('is-busy');
      clearTimeout(noteTimer);
      api('cart/update.js', { note: note ? note.value : '', attributes: attributes })
        .then(freshCheckoutIfTypeChanged)
        .then(function () { window.location.href = checkoutUrl(); })
        .catch(function (err) {
          button.classList.remove('is-busy');
          KM.showError(err);
        });
    }, true);

    // Shopify checkout remembers the address from an earlier visit: after a checkout opened for a
    // shipping address, a local address no longer gets "Local delivery" (and the other way round),
    // until the cart's items change. So when the customer switched between local and shipping since
    // their last checkout, empty the cart and add the same items back (keeping the note, delivery
    // slot and discount codes) before going to checkout again.
    var CHECKOUT_TYPE_KEY = 'km_checkout_type';

    function freshCheckoutIfTypeChanged() {
      var type = isShippingZip() ? 'shipping' : 'local';
      var last = null;
      try { last = localStorage.getItem(CHECKOUT_TYPE_KEY); localStorage.setItem(CHECKOUT_TYPE_KEY, type); } catch (err) {}
      if (!last || last === type) return null;
      return api('cart.js').then(function (cart) {
        if (!cart.items.length) return null;
        var items = cart.items.map(function (item) {
          var line = { id: item.variant_id, quantity: item.quantity, properties: {} };
          Object.keys(item.properties || {}).forEach(function (k) {
            if (item.properties[k] !== null && item.properties[k] !== '') line.properties[k] = item.properties[k];
          });
          if (item.selling_plan_allocation) line.selling_plan = item.selling_plan_allocation.selling_plan.id;
          return line;
        });
        var codes = (cart.discount_codes || []).filter(function (d) { return d.applicable; }).map(function (d) { return d.code; });
        return api('cart/clear.js', {})
          .then(function () { return api('cart/add.js', { items: items }); })
          .then(function () {
            var update = { note: cart.note || '', attributes: cart.attributes || {} };
            if (codes.length) update.discount = codes.join(',');
            return api('cart/update.js', update);
          });
      });
    }

    // /checkout with the zipcode the customer entered on the site (zipcode-handler.js saves it as
    // { zipcode, location: "St. Louis, MO" }), so Shopify checkout opens with zip, city and state
    // already filled in. The customer can still change them there.
    function checkoutUrl() {
      var url = root + 'checkout';
      var saved = null;
      try { saved = JSON.parse(localStorage.getItem('shopify_zipcode') || 'null'); } catch (err) {}
      var zip = saved && String(saved.zipcode || '').replace(/\D/g, '').substring(0, 5);
      if (!zip || zip.length !== 5) return url;
      var params = [['country', 'US'], ['zip', zip]];
      var place = String(saved.location || '').split(',');
      var city = place.length > 1 ? place.slice(0, -1).join(',').trim() : '';
      var state = place.length > 1 ? place[place.length - 1].trim().toUpperCase() : '';
      if (city) params.push(['city', city]);
      if (/^[A-Z]{2}$/.test(state)) params.push(['province', state]);
      return url + '?' + params.map(function (p) {
        return encodeURIComponent('checkout[shipping_address][' + p[0] + ']') + '=' + encodeURIComponent(p[1]);
      }).join('&');
    }

    // "Choose a delivery time": preselect the first day (the calendar app then picks its first
    // free slot). Without a zip the app shows its zip prompt instead, so open that.
    function pickFirstDay() {
      var delivery = dr.el.querySelector('[data-km-dr-delivery]');
      var day = delivery && (delivery.querySelector('.calendar-day.selected') || delivery.querySelector('.calendar-day'));
      if (day && !day.classList.contains('selected')) day.click();
      else if (!day) {
        var zip = delivery && delivery.querySelector('[data-zipcode-prompt]:not(.hidden) [data-popup="zipcode-popup"]');
        if (zip) zip.click();
      }
      if (delivery) delivery.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Special instructions -> cart.note, saved as the customer types (and again at checkout).
    var noteTimer = null;
    dr.el.addEventListener('input', function (e) {
      if (!e.target.matches('[data-km-note]')) return;
      clearTimeout(noteTimer);
      var value = e.target.value;
      noteTimer = setTimeout(function () {
        api('cart/update.js', { note: value }).then(function (cart) { KM.cart = cart; }).catch(function () {});
      }, 700);
    });

    dr.el.addEventListener('change', function (e) {
      if (!e.target.matches('[data-km-receive]')) return;
      api('cart/update.js', { attributes: { 'Delivery preference': e.target.value } }).then(setCart).catch(KM.showError);
    });

    document.addEventListener('zipcode:updated', renderDrawerState);
  }

  // Every way of opening the theme cart (header icon, compact header icon, view-cart bar)
  // opens the v2 drawer instead. Capture phase so the theme's panel handler never runs.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-km-open-cart], a[data-panel="cart"], [data-km-compact-cart]');
    if (!trigger || !dr.el) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    KM.openCart();
  }, true);

  /* ---------- View-cart bar ---------- */

  function renderBar() {
    var bar = document.querySelector('[data-km-bar]');
    if (!bar || !KM.cart) return;
    var count = KM.cart.item_count;
    var show = count > 0 && !document.querySelector('.km-sheet.is-open:not(.km-drawer--page)');
    bar.classList.toggle('is-shown', show);
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    bar.querySelector('[data-km-open-cart]').tabIndex = show ? 0 : -1;
    document.documentElement.classList.toggle('km-has-bar', show);
    bar.querySelector('[data-km-bar-count]').textContent = count + (count === 1 ? ' item' : ' items');
    bar.querySelector('[data-km-bar-total]').textContent = KM.money(KM.cart.total_price);
  }

  document.addEventListener('km:sheet', renderBar);

  // Keep in sync when the theme's own forms (product page, cart page) change the cart.
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var own = init && init.headers && init.headers[OWN_HEADER];
    var external = !own && /\/cart\/(add|change|update|clear)/.test(url);
    var request = nativeFetch.apply(this, arguments);
    if (external) request.then(function () { KM.refresh(); }).catch(function () {});
    return request;
  };

  // Cards inserted after load (e.g. "You may also like", fetched by the theme) need the cart state.
  var cardsQueued = false;
  new MutationObserver(function (mutations) {
    if (cardsQueued || !KM.cart) return;
    var added = mutations.some(function (m) {
      return Array.prototype.some.call(m.addedNodes, function (n) {
        return n.nodeType === 1 && (n.matches('[data-km-card]') || n.querySelector('[data-km-card]'));
      });
    });
    if (!added) return;
    cardsQueued = true;
    requestAnimationFrame(function () { cardsQueued = false; renderCards(); });
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* ---------- Homepage product rows: no empty space at the end (desktop) ----------
     The theme's "offset" rows keep 15% padding on the right so the next card peeks in. At the end
     the last card stopped short of the row's edge, leaving that padding empty. A negative
     slidesOffsetAfter of the same width lets the last card slide up to the edge. */
  function fitRowEnds() {
    if (window.innerWidth < 761) return;
    document.querySelectorAll('.l4cl.s4wi.offset > .swiper-outer').forEach(function (outer) {
      var swiper = outer.swiper;
      if (!swiper || !swiper.params) return;
      var peek = parseFloat(getComputedStyle(outer.parentElement).paddingRight) || 0;
      if (swiper.params.slidesOffsetAfter === -peek) return;
      swiper.params.slidesOffsetAfter = -peek;
      swiper.update();
    });
  }
  // Sliders are built after load, some lazily: check a few times, then on resize.
  var fitTries = 0;
  var fitTimer = setInterval(function () {
    fitRowEnds();
    if (++fitTries > 20) clearInterval(fitTimer);
  }, 750);
  var fitResize = null;
  window.addEventListener('resize', function () {
    clearTimeout(fitResize);
    fitResize = setTimeout(fitRowEnds, 200);
  });

  // Back/forward cache restores stale cards.
  window.addEventListener('pageshow', function (e) { if (e.persisted) KM.refresh(); });

  KM.refresh();
})();
